import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '@/providers/AuthProvider';
import { toast } from 'sonner';
import { Mail, Lock, Eye, EyeOff, ArrowRight, Loader2 } from 'lucide-react';

type LoginStep = 'email' | 'code' | 'password';

export default function LoginPage() {
  const { login, requestAuthCode, loginWithMagicLink } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect') || '/';

  const [step, setStep] = useState<LoginStep>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [usePassword, setUsePassword] = useState(false);

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setIsSubmitting(true);
    try {
      await requestAuthCode(email.trim());
      setStep('code');
      toast.success('Check your email for the verification code');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to send code';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) return;

    setIsSubmitting(true);
    try {
      await loginWithMagicLink(email.trim(), code.trim());
      navigate(redirectTo, { replace: true });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Invalid code';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) return;

    setIsSubmitting(true);
    try {
      await login(email.trim(), password);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Invalid credentials';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOAuth = (provider: 'google' | 'apple') => {
    const baseUrl = import.meta.env.VITE_API_HOST || window.location.origin;
    const callbackUrl = `${window.location.origin}/auth/callback/${provider}`;
    window.location.href = `${baseUrl}/auth/${provider}?redirect_uri=${encodeURIComponent(callbackUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#0B0D10] flex flex-col items-center justify-center px-6">
      {/* Logo */}
      <div className="mb-10 text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-[#A38BFF] to-[#7B68EE] flex items-center justify-center">
          <div className="w-8 h-8 rounded-full bg-[#0B0D10]/30" />
        </div>
        <h1 className="text-3xl font-bold text-[#F5F7FF] tracking-tight">Pulse</h1>
        <p className="text-sm text-[#A7B0C8] mt-1">Everything in one feed</p>
      </div>

      <div className="w-full max-w-sm">
        {/* Magic Link Flow */}
        {!usePassword && step === 'email' && (
          <form onSubmit={handleSendCode} className="space-y-4">
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A7B0C8]" size={18} />
              <input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                autoFocus
                className="w-full h-12 pl-10 pr-4 bg-[#141821] border border-white/[0.08] rounded-xl text-[#F5F7FF] placeholder:text-[#A7B0C8]/50 focus:outline-none focus:border-[#A38BFF]/50 focus:ring-1 focus:ring-[#A38BFF]/30 transition-colors"
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting || !email.trim()}
              className="w-full h-12 bg-gradient-to-r from-[#A38BFF] to-[#7B68EE] text-white font-semibold rounded-xl flex items-center justify-center gap-2 transition-opacity disabled:opacity-50"
            >
              {isSubmitting ? (
                <Loader2 size={20} className="animate-spin" />
              ) : (
                <>
                  Send Verification Code
                  <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>
        )}

        {/* Code Verification Step */}
        {!usePassword && step === 'code' && (
          <form onSubmit={handleVerifyCode} className="space-y-4">
            <p className="text-sm text-[#A7B0C8] text-center mb-2">
              Enter the 6-digit code sent to <span className="text-[#F5F7FF]">{email}</span>
            </p>

            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              placeholder="000000"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              autoFocus
              className="w-full h-14 text-center text-2xl tracking-[0.5em] bg-[#141821] border border-white/[0.08] rounded-xl text-[#F5F7FF] placeholder:text-[#A7B0C8]/30 focus:outline-none focus:border-[#A38BFF]/50 focus:ring-1 focus:ring-[#A38BFF]/30 transition-colors font-mono"
            />

            <button
              type="submit"
              disabled={isSubmitting || code.length < 6}
              className="w-full h-12 bg-gradient-to-r from-[#A38BFF] to-[#7B68EE] text-white font-semibold rounded-xl flex items-center justify-center gap-2 transition-opacity disabled:opacity-50"
            >
              {isSubmitting ? (
                <Loader2 size={20} className="animate-spin" />
              ) : (
                <>
                  Verify
                  <ArrowRight size={18} />
                </>
              )}
            </button>

            <button
              type="button"
              onClick={() => {
                setStep('email');
                setCode('');
              }}
              className="w-full text-sm text-[#A7B0C8] hover:text-[#F5F7FF] transition-colors"
            >
              Use a different email
            </button>
          </form>
        )}

        {/* Password Login */}
        {usePassword && (
          <form onSubmit={handlePasswordLogin} className="space-y-4">
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A7B0C8]" size={18} />
              <input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                autoFocus
                className="w-full h-12 pl-10 pr-4 bg-[#141821] border border-white/[0.08] rounded-xl text-[#F5F7FF] placeholder:text-[#A7B0C8]/50 focus:outline-none focus:border-[#A38BFF]/50 focus:ring-1 focus:ring-[#A38BFF]/30 transition-colors"
              />
            </div>

            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A7B0C8]" size={18} />
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                className="w-full h-12 pl-10 pr-12 bg-[#141821] border border-white/[0.08] rounded-xl text-[#F5F7FF] placeholder:text-[#A7B0C8]/50 focus:outline-none focus:border-[#A38BFF]/50 focus:ring-1 focus:ring-[#A38BFF]/30 transition-colors"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A7B0C8] hover:text-[#F5F7FF]"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            <button
              type="submit"
              disabled={isSubmitting || !email.trim() || !password}
              className="w-full h-12 bg-gradient-to-r from-[#A38BFF] to-[#7B68EE] text-white font-semibold rounded-xl flex items-center justify-center gap-2 transition-opacity disabled:opacity-50"
            >
              {isSubmitting ? (
                <Loader2 size={20} className="animate-spin" />
              ) : (
                <>
                  Sign In
                  <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>
        )}

        {/* Divider */}
        <div className="flex items-center gap-3 my-6">
          <div className="flex-1 h-px bg-white/[0.08]" />
          <span className="text-xs text-[#A7B0C8]">or</span>
          <div className="flex-1 h-px bg-white/[0.08]" />
        </div>

        {/* OAuth Buttons */}
        <div className="space-y-3">
          <button
            onClick={() => handleOAuth('google')}
            className="w-full h-12 bg-[#141821] border border-white/[0.08] rounded-xl text-[#F5F7FF] font-medium flex items-center justify-center gap-3 hover:bg-[#1a2030] transition-colors"
          >
            <svg width="18" height="18" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Continue with Google
          </button>

          <button
            onClick={() => handleOAuth('apple')}
            className="w-full h-12 bg-[#141821] border border-white/[0.08] rounded-xl text-[#F5F7FF] font-medium flex items-center justify-center gap-3 hover:bg-[#1a2030] transition-colors"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
            </svg>
            Continue with Apple
          </button>
        </div>

        {/* Toggle password / magic link */}
        <button
          type="button"
          onClick={() => {
            setUsePassword(!usePassword);
            setStep('email');
            setCode('');
          }}
          className="w-full mt-4 text-sm text-[#A7B0C8] hover:text-[#F5F7FF] transition-colors text-center"
        >
          {usePassword ? 'Use magic link instead' : 'Use password instead'}
        </button>

        {/* Register link */}
        <p className="mt-8 text-center text-sm text-[#A7B0C8]">
          Don't have an account?{' '}
          <Link to="/register" className="text-[#A38BFF] hover:underline font-medium">
            Create Account
          </Link>
        </p>
      </div>
    </div>
  );
}
